import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {ApiAgentService} from '../../services/api-agent.service';
import {UtilsService} from '../../services/utils.service';
import {MerchantDsrRequestItem, MerchantDsrRequestRow} from '../../models/merchant-dsr-request';

/**
 * PDPL Data Subject Requests for the merchant (Roadmap #32, SAMA Obs #1).
 *
 * The merchant exercises a right over their own personal data and can follow the status of every
 * request they have raised. The request is stored as a DSR ticket, so the compliance team sees it
 * in the dashboard's DSR register with a 30-day response deadline.
 *
 * Identity is never sent from here — the API derives the merchant from the JWT.
 */
@Component({
  selector: 'app-data-requests',
  templateUrl: './data-requests.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  styleUrls: ['./data-requests.component.scss']
})
export class DataRequestsComponent implements OnInit {

  loading = true;
  submitting = false;
  formOpen = false;

  requests: MerchantDsrRequestRow[] = [];
  dsrTypes: string[] = [];

  dsrType = '';
  description = '';
  errorMessage = '';
  successMessage = '';

  /** Right -> the label shown to the merchant. Keys are the API's own values. */
  readonly typeLabels: { [key: string]: string } = {
    access: 'Get a copy of my data',
    correction: 'Correct my data',
    deletion: 'Delete my data',
    portability: 'Transfer my data',
    objection: 'Object to processing',
    restriction: 'Restrict processing'
  };

  /**
   * DSR status -> label. Deliberately NOT the bare words Pending/Completed: those keys already
   * carry order-flow translations ("awaiting payment", "delivered") elsewhere in the portal.
   */
  readonly statusLabels: { [key: string]: string } = {
    pending: 'Request received',
    in_progress: 'Being processed',
    completed: 'Request completed',
    rejected: 'Request rejected'
  };

  constructor(private api: ApiAgentService,
              private utils: UtilsService,
              private translate: TranslateService) {
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    try {
      this.dsrTypes = await this.api.getDsrTypes();
      await this.reload();
    } catch (e) {
      this.errorMessage = this.translate.instant('Could not load your requests. Please try again.');
    } finally {
      this.loading = false;
    }
  }

  async reload(): Promise<void> {
    const rows: MerchantDsrRequestItem[] = await this.api.getMyDsrRequests();
    this.requests = rows.map(r => ({
      ...r,
      submittedDate: this.parseApiDate(r.submittedAt),
      deadlineDate: this.parseApiDate(r.responseDeadline)
    }));
  }

  private parseApiDate(value: number | string | null | undefined): Date | null {
    return this.utils.parseApiDate(value);
  }

  openForm(): void {
    this.formOpen = true;
    this.errorMessage = '';
    this.successMessage = '';
  }

  closeForm(): void {
    this.formOpen = false;
    this.dsrType = '';
    this.description = '';
    this.errorMessage = '';
  }

  labelFor(type: string): string {
    return this.typeLabels[type] || type;
  }

  statusLabel(request: MerchantDsrRequestItem): string {
    return this.statusLabels[request.dsrStatus] || request.dsrStatus;
  }

  /**
   * Whole days left against the PDPL deadline; negative once it has passed.
   * Both sides are normalised to local midnight — the deadline arrives as a timestamp, so
   * comparing it raw against midnight rounds a 30-day window up to 31.
   */
  daysLeft(request: MerchantDsrRequestRow): number | null {
    if (!request.deadlineDate) {
      return null;
    }
    const deadline = new Date(request.deadlineDate.getTime());
    deadline.setHours(0, 0, 0, 0);
    const today = new Date().setHours(0, 0, 0, 0);
    return Math.round((deadline.getTime() - today) / (1000 * 60 * 60 * 24));
  }

  isClosed(request: MerchantDsrRequestItem): boolean {
    return request.dsrStatus === 'completed' || request.dsrStatus === 'rejected';
  }

  statusClass(request: MerchantDsrRequestItem): string {
    switch (request.dsrStatus) {
      case 'completed':
        return 'badge-completed';
      case 'rejected':
        return 'badge-rejected';
      case 'in_progress':
        return 'badge-progress';
      default:
        return 'badge-pending';
    }
  }

  async submit(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.dsrType) {
      this.errorMessage = this.translate.instant('Please choose what you would like us to do.');
      return;
    }
    if (!this.description || this.description.trim().length < 10) {
      this.errorMessage = this.translate.instant('Please describe your request in a little more detail.');
      return;
    }

    this.submitting = true;
    try {
      await this.api.saveDsrRequest({dsrType: this.dsrType, description: this.description.trim()});
      this.successMessage = this.translate.instant('Your request has been received. We will respond within 30 days.');
      this.closeForm();
    } catch (e: any) {
      // The API returns a readable reason for the cases the merchant can act on
      // (unknown right, an identical request already open). GatewayException-style errors
      // arrive as {detail}; the endpoint's own validation as {error}.
      const body = e?.error;
      this.errorMessage = (body && (body.error || body.detail))
        ? (body.error || body.detail)
        : this.translate.instant('Could not submit your request. Please try again.');
      this.submitting = false;
      return;
    }
    this.submitting = false;
    // Outside the try: a failed refresh must not report a SAVED request as failed.
    try {
      await this.reload();
    } catch (e) { /* keep the success message; the list refreshes on the next visit */ }
  }
}
